// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Infrastructure.js

import React, { useState } from 'react';
import { FiImage, FiCheck, FiArrowUpRight } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const getImage = (item, apiBaseURL) => {
  const candidate = item.image || item.imageUrl || item.photo || item.banner || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

const InfrastructureCard = ({ item, apiBaseURL }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const resolvedImg = getImage(item, apiBaseURL);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  const showCategory = item.category && item.category !== 'General' && item.category.trim() !== '';

  return (
    <div 
      className="group relative bg-white rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 flex flex-col h-full"
      style={{ fontFamily: fontBody }}
    >
      {/* Image Wrap */}
      <div className="relative aspect-[16/10] overflow-hidden w-full bg-gray-50">
        {resolvedImg && !imageError ? (
          <>
            <img
              src={resolvedImg}
              alt={item.title}
              className="w-full h-full object-cover transform transition-transform duration-1000 ease-out group-hover:scale-105"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              loading="lazy"
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 animate-pulse" />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FiImage className="w-10 h-10 opacity-30" style={{ color: primary }} />
          </div>
        )}
        
        {showCategory && (
          <div 
            className="absolute top-4 left-4 px-3.5 py-1.5 backdrop-blur-md rounded-full text-[11px] font-semibold tracking-wider uppercase text-white shadow-sm"
            style={{ backgroundColor: `${primary}e6` }}
          >
            {item.category}
          </div>
        )}
      </div>

      {/* Content Space */}
      <div className="p-6 md:p-8 flex flex-col flex-grow">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h4 
            className="text-xl md:text-2xl font-bold tracking-tight text-gray-900 group-hover:text-opacity-80 transition-colors"
            style={{ fontFamily: fontHeading }}
          >
            {item.title}
          </h4>
          <div 
            className="p-2 rounded-full opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all duration-300 hidden md:block"
            style={{ backgroundColor: `${primary}08`, color: primary }}
          >
            <FiArrowUpRight className="w-4 h-4" />
          </div>
        </div>
        
        {item.description && (
          <p className="text-sm text-gray-500 leading-relaxed mb-6 font-normal">
            {item.description}
          </p>
        )}

        {/* Features list aligns nicely at the bottom */}
        {item.features && Array.isArray(item.features) && item.features.length > 0 && (
          <div className="mt-auto pt-4 border-t border-gray-50 flex flex-wrap gap-2">
            {item.features.slice(0, 3).map((feature, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-gray-600 transition-colors duration-300"
                style={{ backgroundColor: `${primary}05` }}
              >
                <FiCheck className="w-3 h-3 flex-shrink-0" style={{ color: secondary || primary }} />
                {feature}
              </span>
            ))}
            {item.features.length > 3 && (
              <span 
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-gray-400"
                style={{ backgroundColor: 'transparent', border: `1px dashed ${primary}20` }}
              >
                +{item.features.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Infrastructure = ({ infrastructure, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'grid', items = [] } = infrastructure || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  // Modern crisp structural styles
  const gridClasses = {
    'grid': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10',
    'carousel': 'grid grid-cols-1 md:grid-cols-3 gap-6 overflow-x-auto pb-4 snap-x',
    'list': 'grid grid-cols-1 max-w-3xl mx-auto gap-8',
  };

  return (
    <section id="infrastructure" className="py-20 md:py-28 bg-white selection:bg-gray-100">
      <div className="container mx-auto px-6 max-w-7xl">
        
        {/* Modern Minimalistic Header Block */}
        {(sectionTitle || sectionSubtitle) && (
          <div className="max-w-3xl mb-16 md:mb-20">
            <span 
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase mb-4"
              style={{ color: secondary || primary, fontFamily: fontBody }}
            >
              Campus Ecosystem
            </span>
            {sectionTitle && (
              <h2 
                className="text-3xl md:text-5xl font-black tracking-tight mb-5 text-gray-900 balance"
                style={{ fontFamily: fontHeading }}
              >
                {sectionTitle}
              </h2>
            )}
            {sectionSubtitle && (
              <p 
                className="text-base md:text-lg text-gray-500 font-normal leading-relaxed max-w-2xl"
                style={{ fontFamily: fontBody }}
              >
                {sectionSubtitle}
              </p>
            )}
          </div>
        )}

        {/* Content Showcase Area */}
        <div className={gridClasses[displayStyle] || gridClasses['grid']}>
          {items.map((item) => (
            <div key={item._id || item.id || item.title} className={displayStyle === 'carousel' ? 'snap-start' : ''}>
              <InfrastructureCard 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Infrastructure;